import React, { useState } from 'react';
import { X, DollarSign } from 'lucide-react';

interface PaymentAmountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => void;
}

export default function PaymentAmountModal({
  isOpen,
  onClose,
  onConfirm
}: PaymentAmountModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Quick amount buttons (similar to TopupPage)
  const quickAmounts = [100000, 500000, 1000000, 5000000];

  const handleAmountChange = (value: string) => {
    setAmount(value);
    setError('');
  };

  const handleQuickAmount = (value: number) => {
    setAmount(value.toString());
    setError('');
  };

  const handleConfirm = () => {
    // Validate amount
    const numAmount = parseFloat(amount);

    if (!amount || isNaN(numAmount)) {
      setError('Vui lòng nhập số tiền hợp lệ');
      return;
    }

    if (numAmount <= 0) {
      setError('Số tiền phải lớn hơn 0');
      return;
    }

    // Call onConfirm callback
    onConfirm(numAmount);

    // Reset form
    setAmount('');
    setError('');
  };

  const handleClose = () => {
    setAmount('');
    setError('');
    onClose();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN').format(value) + ' VND';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600" />
            Nạp tiền qua QR
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Nhập số tiền cần nạp vào hệ thống
          </p>
        </div>

        {/* Amount Input */}
        <div className="mb-4">
          <label
            htmlFor="amount"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Số tiền (VND)
          </label>
          <input
            id="amount"
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="Nhập số tiền"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none text-lg ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
            min="0"
            step="1000"
          />
          {error && (
            <p className="text-red-500 text-sm mt-1">{error}</p>
          )}
        </div>

        {/* Quick Amount Buttons */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">Chọn nhanh:</p>
          <div className="grid grid-cols-2 gap-2">
            {quickAmounts.map((quickAmount) => (
              <button
                key={quickAmount}
                onClick={() => handleQuickAmount(quickAmount)}
                className={`px-4 py-2 border rounded-lg text-sm font-medium transition ${
                  amount === quickAmount.toString()
                    ? 'bg-green-100 border-green-500 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {(quickAmount / 1000).toLocaleString('vi-VN')}K
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-gray-600">Số tiền sẽ nạp:</p>
            <p className="text-2xl font-bold text-green-700">
              {formatCurrency(parseFloat(amount))}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition"
          >
            Hủy
          </button>
          <button
            onClick={handleConfirm}
            disabled={!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition"
          >
            Tiếp tục
          </button>
        </div>

        {/* Help Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Sau khi xác nhận, bạn sẽ thấy mã QR để quét và thanh toán
        </p>
      </div>
    </div>
  );
}
